<template>
  <div v-if="solutionInfo">
    
    <!-- 솔루션 선택 다이얼로그 -->
    <Transition name="modal">
      <div v-if="showSolutionPicker" class="solution-picker-overlay">
        <div class="solution-picker-backdrop" @click="showSolutionPicker = false"></div>
        <div class="solution-picker-dialog">
          <div class="picker-header">
            <h3>비교할 솔루션 선택</h3>
            <button class="close-btn" @click="showSolutionPicker = false">
              <i class="ph-bold ph-x"></i>
            </button>
          </div>
          <div class="picker-search">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="solutionSearchQuery" 
              placeholder="솔루션 이름 또는 고객명 검색..."
            >
          </div>
          <div class="picker-list">
            <div 
              v-for="sol in filteredSolutions" 
              :key="sol.idx"
              class="picker-item"
              :class="{ disabled: sol.idx === solutionInfo.idx }"
              @click="selectComparisonSolution(sol)"
            >
              <div class="picker-item-info">
                <span class="sol-name">{{ sol.name }}</span>
                <span class="sol-customer">{{ sol.customer_name || '미지정 고객' }}</span>
              </div>
              <div class="picker-item-meta">
                <span class="sol-type">{{ sol.type === 0 ? '제안' : '분석' }}</span>
                <span class="sol-date">{{ sol.updated_at?.slice(0, 10) }}</span>
              </div>
            </div>
            <div v-if="filteredSolutions.length === 0" class="picker-empty">
              검색 결과가 없습니다
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <div id="module-CA-view" :class="[
      p_solution.type == 0 ? 'CA_A' : 'CA_B', 
      printMode === 'excel' ? 'print-excel' : '',
      compareMode ? 'compare-mode' : '',
      feedbackMode ? 'feedback-mode' : ''
    ]">

      <!-- ========================================
           피드백 캔버스 오버레이
      ======================================== -->
      <div 
        v-if="feedbackMode" 
        class="feedback-canvas-container"
        :class="{ 'view-only': feedbackMode === 'view' }"
      >
        <!-- 캔버스 레이어 -->
        <canvas 
          ref="feedbackCanvas"
          class="feedback-canvas"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
          @touchstart.prevent="handleTouchStart"
          @touchmove.prevent="handleTouchMove"
          @touchend.prevent="stopDrawing"
        ></canvas>

        <!-- 피드백 툴바 (편집 모드에서만) -->
        <div v-if="feedbackMode !== 'view'" class="feedback-toolbar">
          <div class="toolbar-section tools">
            <button 
              v-for="tool in feedbackTools" 
              :key="tool.id"
              class="tool-btn"
              :class="{ active: currentTool === tool.id }"
              @click="selectTool(tool.id)"
              :title="tool.name"
            >
              <i :class="tool.icon"></i>
            </button>
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-section colors">
            <button 
              v-for="color in toolColors" 
              :key="color"
              class="color-btn"
              :class="{ active: currentColor === color }"
              :style="{ background: color }"
              @click="currentColor = color"
            ></button>
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-section sizes">
            <button 
              v-for="size in brushSizes" 
              :key="size"
              class="size-btn"
              :class="{ active: currentSize === size }"
              @click="currentSize = size"
            >
              <span :style="{ width: size + 'px', height: size + 'px' }"></span>
            </button>
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-section actions">
            <button class="action-btn undo" @click="undoAnnotation" title="실행취소">
              <i class="ph-bold ph-arrow-counter-clockwise"></i>
            </button>
            <button class="action-btn clear" @click="clearAllAnnotations" title="전체 지우기">
              <i class="ph-bold ph-trash"></i>
            </button>
          </div>
        </div>

        <!-- 피드백 코멘트 패널 -->
        <div class="feedback-comment-panel" :class="{ expanded: commentPanelExpanded }">
          <div class="panel-header" @click="commentPanelExpanded = !commentPanelExpanded">
            <span>
              <i class="ph-bold ph-chat-circle-text"></i>
              피드백 코멘트
            </span>
            <i :class="commentPanelExpanded ? 'ph ph-caret-down' : 'ph ph-caret-up'"></i>
          </div>
          
          <div class="panel-body" v-if="commentPanelExpanded">
            <textarea 
              v-if="feedbackMode !== 'view'"
              v-model="feedbackComment"
              placeholder="피드백 내용을 입력하세요...&#10;&#10;예시:&#10;- 암 진단비 보장이 부족합니다&#10;- 후유장해 보장을 추가 검토하세요"
              rows="5"
            ></textarea>
            <div v-else class="comment-view">
              <p v-if="feedbackComment">{{ feedbackComment }}</p>
              <p v-else class="no-comment">작성된 코멘트가 없습니다.</p>
            </div>
          </div>
        </div>

        <!-- 피드백 완료 버튼 (편집 모드에서만) -->
        <div v-if="feedbackMode !== 'view'" class="feedback-actions">
          <button class="feedback-save-btn" @click="saveFeedbackDraft">
            <i class="ph-bold ph-floppy-disk"></i>
            임시저장
          </button>
          <button class="feedback-complete-btn" @click="completeFeedback">
            <i class="ph-bold ph-check-circle"></i>
            피드백 완료
          </button>
        </div>

        <!-- 피드백 보기 모드 닫기 버튼 -->
        <div v-if="feedbackMode === 'view'" class="feedback-view-actions">
          <div class="feedback-info-badge">
            <i class="ph-fill ph-eye"></i>
            피드백 보기 모드
          </div>
        </div>
      </div>

      <!-- #### MAIN HEADER #### start -->
      <div class="main-header">
        <div>
          <img src='/imgs/LOGO.png'>
          <h2 v-if="solutionInfo.type == 0">보장 스케치(제안)</h2>
          <h2 v-if="solutionInfo.type == 1">보장 스케치(분석)</h2>
          <!-- 피드백 모드 표시 -->
          <span v-if="feedbackMode" class="feedback-mode-badge">
            <i class="ph-bold ph-pencil-circle"></i>
            {{ feedbackMode === 'view' ? '피드백 보기' : '피드백 작성 중' }}
          </span>
        </div>
        <p @click="closeView"><i class="ph-bold ph-x"></i></p>
      </div>
      <!-- #### MAIN HEADER #### end -->

      <!-- #### HEADER #### start -->
      <div class="header">
        <div class="left">
          <div>
            <span>{{ solutionInfo.name }}</span>
          </div>
          <div class="profile">
            <img :src="basic_profile_url" />
            <span>{{ connectedCustomer?.name || solutionInfo.client?.name }}<small>님</small></span>
            <span>({{ genderChoser(connectedCustomer?.rrn?.split('-')[1]?.slice(0,1)) || genderChoser(solutionInfo.client.civ_code?.split('-')[1]?.slice(0,1)) || '미입력' }})</span>
            <span>{{ calculateInsuAge(connectedCustomer?.rrn?.split('-')[0]) + '세' || calculateInsuAge(solutionInfo.client.civ_code?.split('-')[0]) + '세' || '미입력' }} <small>(보험나이)</small></span>
          </div>
          <div>
            <span>직업등급: {{ connectedCustomer?.job_grade || solutionInfo.client?.grade }}급</span>
          </div>
        </div>
        <div class="right" v-if="!feedbackMode">
          <!-- 비교 모드 토글 -->
          <div class="compare-toggle" :class="{ active: compareMode }">
            <button 
              class="select-compare-btn"
              @click="openSolutionPicker"
              :title="comparisonSolution ? '비교 대상: ' + comparisonSolution.name : '비교할 솔루션 선택'"
            >
              <i class="ph ph-caret-down"></i>
              <span v-if="comparisonSolution" class="compare-target">{{ comparisonSolution.name }}</span>
              <span v-else>솔루션 선택</span>
            </button>
          </div>
          
          <button @click="printSolution('card')" class="btn_a" title="카드형 인쇄">
            <i class="ph-bold ph-squares-four"></i> 카드형 인쇄
          </button>
          <button @click="printSolution('excel')" class="btn_a" title="표형 인쇄">
            <i class="ph-bold ph-table"></i> 엑셀형 인쇄
          </button>
        </div>
      </div>
      <!-- #### HEADER #### end -->

      <span id="etc-notice-sp">단위:만원</span>

      <!-- #### content/chapter #### start -->
      <div class="contents" v-if="chapterList">
        <div v-for="(chapter, key) in chapterList" :key="chapter.id || key" class="chapter"
             :class="{ fold: chapter.fold, wide: chapter.idx == '7' }">
          <h2 class="head" @click="foldOpenByKey(key)">
            {{ chapter.name }}
            <!-- 비교 모드: 챕터별 변경 수 표시 -->
            <span v-if="compareMode" class="chapter-diff-badge">
              <span v-if="getChapterDiff(chapter).added > 0" class="added">+{{ getChapterDiff(chapter).added }}</span>
              <span v-if="getChapterDiff(chapter).removed > 0" class="removed">-{{ getChapterDiff(chapter).removed }}</span>
              <span v-if="getChapterDiff(chapter).changed > 0" class="changed">⟳{{ getChapterDiff(chapter).changed }}</span>
            </span>
          </h2>
          <ul class="child" v-if="!chapter.fold">
            <template v-for="(item, j) in chapter.list" :key="j">
              <li :class="j" v-if="item.delegate === 1">
                <span>{{ item.name }}</span>
              </li>
            </template>
          </ul>
        </div>
      </div>
      <!-- #### content/chapter #### end -->

      <!-- #### main (카드형) #### start -->
      <div class="chart" ref="chartContainer">
        <!-- 비교 모드 Sticky 배너 -->
        <Transition name="slide-down">
          <div v-if="compareMode && comparisonSolution" class="compare-sticky-bar">
            <div class="compare-bar-content">
              <!-- 좌측: 기준 vs 비교 -->
              <div class="compare-bar-sides">
                <div class="bar-side base">
                  <span class="side-label">기준</span>
                  <span class="side-name">{{ solutionInfo.name }}</span>
                  <span class="side-price">{{ formatMoney(getSolutionPrice()) }}<small>원</small></span>
                </div>
                <div class="bar-vs">
                  <i class="ph-bold ph-arrow-right"></i>
                </div>
                <div class="bar-side target">
                  <span class="side-label">비교</span>
                  <span class="side-name">{{ comparisonSolution.name }}</span>
                  <span class="side-price">{{ formatMoney(getComparisonSolutionPrice()) }}<small>원</small></span>
                </div>
              </div>
              
              <!-- 중앙: 통계 -->
              <div class="compare-bar-stats">
                <div class="bar-stat added" v-if="comparisonStats.added > 0">
                  <i class="ph-fill ph-plus-circle"></i>
                  <span>{{ comparisonStats.added }}</span>
                </div>
                <div class="bar-stat removed" v-if="comparisonStats.removed > 0">
                  <i class="ph-fill ph-minus-circle"></i>
                  <span>{{ comparisonStats.removed }}</span>
                </div>
                <div class="bar-stat changed" v-if="comparisonStats.changed > 0">
                  <i class="ph-fill ph-swap"></i>
                  <span>{{ comparisonStats.changed }}</span>
                </div>
              </div>

              <!-- 우측: 가격 차이 -->
              <div class="compare-bar-diff" :class="{ increase: priceDiff > 0, decrease: priceDiff < 0, same: priceDiff === 0 }">
                <i :class="priceDiff > 0 ? 'ph-bold ph-trend-up' : priceDiff < 0 ? 'ph-bold ph-trend-down' : 'ph-bold ph-equals'"></i>
                <span>{{ priceDiff > 0 ? '+' : '' }}{{ formatMoney(priceDiff) }}원</span>
              </div>

              <!-- 범례 토글 -->
              <div class="compare-bar-legend">
                <div class="legend-dot added" title="추가된 보장"></div>
                <div class="legend-dot removed" title="제거된 보장"></div>
                <div class="legend-dot changed-up" title="보장 증가"></div>
                <div class="legend-dot changed-down" title="보장 감소"></div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- 기준 솔루션 플랜들 -->
        <template v-if="solution.plan_list && solution.plan_list.length > 0">
          <div v-for="(plan, i) in solution.plan_list" v-bind:key="'base-'+i" class="plan">
            
            <!-- 증권 정보 -->
            <div class="plan-info">
              <ul>
                <li><img :src="getInsuCompanyInfo(plan.company).short_img"></li>
                <li><h2>{{ getInsuCompanyInfo(plan.company).value == '' ? plan.company_etc : getInsuCompanyInfo(plan.company).name }}</h2></li>
                <li><span v-if="plan.type_id == 0">{{ plan.type }}</span><span v-if="plan.type_id != 0">{{ getTypeTypeLabel(plan.type_id) }}</span></li>
                <li><span>{{ plan.pay_type < 2 ? plan.pay : '' }}{{ getPayTypeLabel(plan.pay_type) }} / {{ plan.end_type < 2 ? plan.end : '' }}{{ getEndTypeLabel(plan.end_type) }}</span></li>
                <li><span>가입:{{ plan.joined_at }}</span></li>
                <li><h2>{{ formatMoney(plan.price) }}원</h2></li>
              </ul>
              <span v-if="compareMode" class="plan-label base-label">기준</span>
            </div>
            
            <!-- 보장 내용 -->
            <div class="coverage-list">
              <div v-for="(chapter, key) in chapterList" 
                   :key="chapter.id || key" 
                   class="coverage"
                   :class="{ fold: chapter.fold, wide: chapter.idx == '7' }">

                <div class="list" v-if="chapter.fold">
                  <ul class="dual-stack">
                    <template v-for="(child, j) in chapter.list" :key="j">
                      <template v-for="(item, k) in returnCoverages(plan.fake_idx, child.idx).filter(c => c.use).sort((a, b) => a.no - b.no)" :key="k">
                        <li :title="item.long_name" 
                            v-if="!item?.low_show" 
                            :class="[
                              { important: item?.style_important, bold: item?.style_bold },
                              compareMode ? getCoverageCompareStatus(item, child.idx) : ''
                            ]">
                          <span>{{ halfLettering ? (item.short_name || item.name.slice(0, 4)) : (item.short_name || item.name.slice(0, 4)) }}</span>
                          <span>
                            {{ formatMoney(item.value) }}
                            <template v-if="compareMode && getCoverageCompareStatus(item, child.idx) === 'removed'">
                              <i class="ph-fill ph-minus-circle diff-icon"></i>
                            </template>
                          </span>
                        </li>
                      </template>
                    </template>
                  </ul>
                  <div class="other" v-if="getHiddenCountByChapter(plan.fake_idx, chapter) > 0">
                    <span>미표시</span>
                    <span>{{ getHiddenCountByChapter(plan.fake_idx, chapter) }}건</span>
                  </div>
                  <span class="none" v-if="getCovsInChapter(plan, chapter).length === 0 && getHiddenCountByChapter(plan.fake_idx, chapter) == 0">
                    보장 없음
                  </span>
                </div>

                <template v-for="(item, j) in chapter.list" :key="j">
                  <div class="list folder" v-if="!chapter.fold">
                    <ul class="dual-stack">
                      <template v-for="(coverage, k) in returnCoverages(plan.fake_idx, item.idx).sort((a, b) => a.no - b.no)" :key="k">
                        <li :title="coverage.long_name" 
                            v-if="!coverage.is_union && !coverage?.low_show" 
                            :class="[
                              { important: coverage?.style_important, bold: coverage?.style_bold },
                              compareMode ? getCoverageCompareStatus(coverage, item.idx) : ''
                            ]">
                          <span>{{ halfLettering ? coverage.name : (coverage.short_name || coverage.name.slice(0, 4)) }}</span>
                          <span>
                            {{ formatMoney(coverage.value) }}
                            <template v-if="compareMode && getCoverageCompareStatus(coverage, item.idx) === 'removed'">
                              <i class="ph-fill ph-minus-circle diff-icon"></i>
                            </template>
                          </span>
                        </li>
                      </template>
                    </ul>
                    <div class="other" v-if="getHiddenCount(plan.fake_idx, item.idx) > 0 && chapter.fold">
                      <span>미표시</span>
                      <span>{{ getHiddenCount(plan.fake_idx, item.idx) }}건</span>
                    </div>
                    <span class="none" v-if="returnCoverages(plan.fake_idx, item.idx).length === 0 && getHiddenCount(plan.fake_idx, item.idx) == 0">
                      보장 없음
                    </span>
                  </div>
                </template>
              </div>
            </div>

          </div>
        </template>

        <!-- 비교 솔루션 플랜들 (비교 모드일 때만) -->
        <template v-if="compareMode && comparisonPlanList.length > 0">
          <div class="compare-divider">
            <span>비교 솔루션: {{ comparisonSolution.name }}</span>
          </div>
          
          <div v-for="(plan, i) in comparisonPlanList" v-bind:key="'compare-'+i" class="plan compare-plan">
            
            <!-- 증권 정보 -->
            <div class="plan-info">
              <ul>
                <li><img :src="getInsuCompanyInfo(plan.company).short_img"></li>
                <li><h2>{{ getInsuCompanyInfo(plan.company).value == '' ? plan.company_etc : getInsuCompanyInfo(plan.company).name }}</h2></li>
                <li><span v-if="plan.type_id == 0">{{ plan.type }}</span><span v-if="plan.type_id != 0">{{ getTypeTypeLabel(plan.type_id) }}</span></li>
                <li><span>{{ plan.pay_type < 2 ? plan.pay : '' }}{{ getPayTypeLabel(plan.pay_type) }} / {{ plan.end_type < 2 ? plan.end : '' }}{{ getEndTypeLabel(plan.end_type) }}</span></li>
                <li><span>가입:{{ plan.joined_at }}</span></li>
                <li><h2>{{ formatMoney(plan.price) }}원</h2></li>
              </ul>
              <span class="plan-label compare-label">비교</span>
            </div>
            
            <!-- 보장 내용 -->
            <div class="coverage-list">
              <div v-for="(chapter, key) in chapterList" 
                   :key="chapter.id || key" 
                   class="coverage"
                   :class="{ fold: chapter.fold, wide: chapter.idx == '7' }">

                <div class="list" v-if="chapter.fold">
                  <ul class="dual-stack">
                    <template v-for="(child, j) in chapter.list" :key="j">
                      <template v-for="(item, k) in returnComparisonCoverages(plan.fake_idx, child.idx).filter(c => c.use).sort((a, b) => a.no - b.no)" :key="k">
                        <li :title="item.long_name" 
                            v-if="!item?.low_show" 
                            :class="[
                              { important: item?.style_important, bold: item?.style_bold },
                              getComparisonCoverageStatus(item, child.idx)
                            ]">
                          <span>{{ halfLettering ? (item.short_name || item.name.slice(0, 4)) : (item.short_name || item.name.slice(0, 4)) }}</span>
                          <span>
                            {{ formatMoney(item.value) }}
                            <template v-if="getComparisonCoverageStatus(item, child.idx) === 'added'">
                              <i class="ph-fill ph-plus-circle diff-icon"></i>
                            </template>
                            <template v-if="getComparisonCoverageStatus(item, child.idx) === 'changed-up'">
                              <i class="ph-fill ph-arrow-circle-up diff-icon"></i>
                            </template>
                            <template v-if="getComparisonCoverageStatus(item, child.idx) === 'changed-down'">
                              <i class="ph-fill ph-arrow-circle-down diff-icon"></i>
                            </template>
                          </span>
                        </li>
                      </template>
                    </template>
                  </ul>
                  <div class="other" v-if="getComparisonHiddenCountByChapter(plan.fake_idx, chapter) > 0">
                    <span>미표시</span>
                    <span>{{ getComparisonHiddenCountByChapter(plan.fake_idx, chapter) }}건</span>
                  </div>
                  <span class="none" v-if="getComparisonCovsInChapter(plan, chapter).length === 0 && getComparisonHiddenCountByChapter(plan.fake_idx, chapter) == 0">
                    보장 없음
                  </span>
                </div>

                <template v-for="(item, j) in chapter.list" :key="j">
                  <div class="list folder" v-if="!chapter.fold">
                    <ul class="dual-stack">
                      <template v-for="(coverage, k) in returnComparisonCoverages(plan.fake_idx, item.idx).sort((a, b) => a.no - b.no)" :key="k">
                        <li :title="coverage.long_name" 
                            v-if="!coverage.is_union && !coverage?.low_show" 
                            :class="[
                              { important: coverage?.style_important, bold: coverage?.style_bold },
                              getComparisonCoverageStatus(coverage, item.idx)
                            ]">
                          <span>{{ halfLettering ? coverage.name : (coverage.short_name || coverage.name.slice(0, 4)) }}</span>
                          <span>
                            {{ formatMoney(coverage.value) }}
                            <template v-if="getComparisonCoverageStatus(coverage, item.idx) === 'added'">
                              <i class="ph-fill ph-plus-circle diff-icon"></i>
                            </template>
                            <template v-if="getComparisonCoverageStatus(coverage, item.idx) === 'changed-up'">
                              <i class="ph-fill ph-arrow-circle-up diff-icon"></i>
                            </template>
                            <template v-if="getComparisonCoverageStatus(coverage, item.idx) === 'changed-down'">
                              <i class="ph-fill ph-arrow-circle-down diff-icon"></i>
                            </template>
                          </span>
                        </li>
                      </template>
                    </ul>
                    <div class="other" v-if="getComparisonHiddenCount(plan.fake_idx, item.idx) > 0 && chapter.fold">
                      <span>미표시</span>
                      <span>{{ getComparisonHiddenCount(plan.fake_idx, item.idx) }}건</span>
                    </div>
                    <span class="none" v-if="returnComparisonCoverages(plan.fake_idx, item.idx).length === 0 && getComparisonHiddenCount(plan.fake_idx, item.idx) == 0">
                      보장 없음
                    </span>
                  </div>
                </template>
              </div>
            </div>

          </div>
        </template>
      </div>
      <!-- #### main (카드형) #### end -->

      <!-- #### 표형 인쇄용 테이블 #### start -->
      <div class="excel-table">
        <div class="excel-header">
          <div class="excel-title">
            <h1>보장 스케치 ({{ solutionInfo.type == 0 ? '제안' : '분석' }})</h1>
          </div>
          <div class="excel-info">
            <span>주피보험자: <strong>{{ connectedCustomer?.name || solutionInfo.client?.name }}</strong> 님</span>
            <span class="unit">단위: 만원</span>
          </div>
        </div>

        <table class="excel-unified-table">
          <thead>
            <tr class="header-main">
              <th rowspan="2" class="col-plan">보험상품</th>
              <th rowspan="2" class="col-narrow">사망보장<br><small>(상해,질병)</small></th>
              <th rowspan="2" class="col-narrow">후유장해<br><small>(상해,질병)</small></th>
              <th colspan="3" class="col-diagnosis">진단비<br><small>(3대 주요질병)</small></th>
              <th rowspan="2" class="col-coverage">수술비<br><small>(기본,주요)</small></th>
              <th rowspan="2" class="col-coverage">입원비<br><small>(기본,주요)</small></th>
              <th rowspan="2" class="col-coverage">치료비<br><small>(기본,주요)</small></th>
              <th rowspan="2" class="col-coverage">생활보장<br><small>(다빈도,필수)</small></th>
              <th rowspan="2" class="col-narrow">기타보장<br><small>(저비용,미분류)</small></th>
            </tr>
            <tr class="header-sub">
              <th class="col-sub">암</th>
              <th class="col-sub">뇌</th>
              <th class="col-sub">심</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(plan, i) in solution.plan_list" :key="i">
              <td class="plan-cell">
                <div class="plan-info-compact">
                  <strong>{{ getInsuCompanyInfo(plan.company).name || plan.company_etc }}</strong>
                  <span>{{ plan.type }}</span>
                  <small>{{ plan.joined_at }}</small>
                  <small>{{ plan.pay }}{{ getPayTypeShort(plan.pay_type) }}-{{ plan.end }}{{ getEndTypeShort(plan.end_type) }}</small>
                  <em>{{ formatMoney(plan.price) }}원</em>
                </div>
              </td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'death')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'disability')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'cancer')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'brain')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'heart')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'surgery')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'hospital')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'treatment')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'living')"></div></td>
              <td><div class="cell-stack" v-html="getDelegateValues(plan.fake_idx, 'etc')"></div></td>
            </tr>
          </tbody>
        </table>

        <div class="excel-footer">
          <span>BODA</span>
          <span>상담사: {{ member_info.name }}</span>
        </div>
      </div>
      <!-- #### 표형 인쇄용 테이블 #### end -->

      <!-- #### FOOTER #### start -->
      <div class="footer closed" :class="{ 'feedback-active': feedbackMode }">
        <div class="open_btn" @click="openFooter"><span></span></div>
        <div class="short">
          <ul style="flex:1; justify-content:flex-start;">
            <li><span>합계</span><h2>{{ formatMoney(getSolutionPrice()) }}원</h2></li>
            <li v-if="compareMode && comparisonSolution">
              <span style="color: var(--color-brand)">vs</span>
              <h2 style="color: var(--color-brand)">{{ formatMoney(getComparisonSolutionPrice()) }}원</h2>
              <span :class="{ 'price-up': priceDiff > 0, 'price-down': priceDiff < 0 }">
                ({{ priceDiff > 0 ? '+' : '' }}{{ formatMoney(priceDiff) }})
              </span>
            </li>
          </ul>
          <span style="flex:1; text-align:center; color:var(--color-text-primary); font-size:14px; font-weight:800; opacity:0.5">미표시 항목은 첨부된 상세내역을 참조</span>
          <ul style="flex:1; justify-content:flex-end;">
            <li><span>플래너</span><h2>{{ member_info.name }}</h2></li>
          </ul>
        </div>
        <ul>
          <li v-for="chapter in chapterList" :key="chapter.idx">
            <template v-if="resultCovsByChapter[chapter.idx]">
              <div class="dual-stack">
                <template v-for="(mid, midKey) in resultCovsByChapter[chapter.idx].items" :key="midKey">
                  <div v-for="item in (mid.list || [])" :key="item.idx">
                    <span>{{ item.short_name || (item.name && item.name.slice(0,4)) || item.name }}</span>
                    <span>{{ formatMoney(getResultFromUnionsChild(item)) }}</span>
                  </div>
                </template>
              </div>
            </template>
          </li>
        </ul>
      </div>
      <!-- #### FOOTER #### end -->

    </div>
  </div>
</template>

<script>
import { ref, inject, reactive, onMounted, computed, watch, nextTick, onBeforeUnmount } from 'vue';

export default {
  props: {
    closeFunction: Function,
    p_solution: Object,
  },
  setup(props) {
    const solutionInfo = ref(props.p_solution);
    if (!solutionInfo.value) return {};

    const sql_query = inject('sql_query');
    const getInsuCompanyInfo = inject('getInsuCompanyInfo');
    const member_info = inject('member_info');
    const calculateInsuAge = inject('calculateInsuAge');
    
    const coverageList = reactive([]);
    const chapterList = ref({});
    const solution = ref({ plan_list: [] });
    const connectedCustomer = ref(null);
    const basic_profile_url = `/imgs/unknow_user.png`;
    const halfLettering = ref(window.innerWidth >= 1120);
    const resultCovsByChapter = ref({});
    const printMode = ref('card');
    const chartContainer = ref(null);

    // 비교 모드 상태
    const compareMode = ref(false);
    const showSolutionPicker = ref(false);
    const solutionSearchQuery = ref('');
    const availableSolutions = ref([]);
    const comparisonSolution = ref(null);
    const comparisonPlanList = ref([]);
    const comparisonCoverageList = reactive([]);

    // ========================================
    // 피드백 시스템 상태
    // ========================================
    const feedbackMode = ref(props.p_solution.feedback_mode || false);
    const feedbackIdx = ref(props.p_solution.feedback_idx || null);
    const feedbackCanvas = ref(null);
    const feedbackComment = ref(props.p_solution.comment || '');
    const commentPanelExpanded = ref(true);
    
    // 캔버스 드로잉 상태
    const isDrawing = ref(false);
    const currentTool = ref('pen');
    const currentColor = ref('#ef4444');
    const currentSize = ref(3);
    const annotations = ref([]);
    const currentPath = ref([]);
    const canvasContext = ref(null);

    // 피드백 도구 정의
    const feedbackTools = [
      { id: 'pen', name: '펜', icon: 'ph-bold ph-pen' },
      { id: 'highlighter', name: '형광펜', icon: 'ph-bold ph-highlighter' },
      { id: 'arrow', name: '화살표', icon: 'ph-bold ph-arrow-up-right' },
      { id: 'rect', name: '사각형', icon: 'ph-bold ph-rectangle' },
      { id: 'text', name: '텍스트', icon: 'ph-bold ph-text-t' },
      { id: 'eraser', name: '지우개', icon: 'ph-bold ph-eraser' },
    ];

    // 색상 팔레트
    const toolColors = [
      '#ef4444', // red
      '#f59e0b', // amber
      '#22c55e', // green
      '#3b82f6', // blue
      '#8b5cf6', // purple
      '#000000', // black
    ];

    // 브러시 크기
    const brushSizes = [2, 4, 6, 10];

    // 챕터 영문명 매핑
    const chapterMapping = {
      death: 'death',
      disability: 'aftereffect',
      cancer: 'three_major_cancer',
      brain: 'three_major_brain',
      heart: 'three_major_heart',
      surgery: 'surgery',
      hospital: 'hosp_fee',
      treatment: 'medical',
      living: 'living',
      etc: 'other',
    };

    // 필터된 솔루션 목록
    const filteredSolutions = computed(() => {
      if (!solutionSearchQuery.value) return availableSolutions.value;
      const query = solutionSearchQuery.value.toLowerCase();
      return availableSolutions.value.filter(sol => 
        sol.name.toLowerCase().includes(query) ||
        (sol.customer_name && sol.customer_name.toLowerCase().includes(query))
      );
    });

    // 비교 통계
    const comparisonStats = computed(() => {
      if (!compareMode.value || comparisonCoverageList.length === 0) {
        return { added: 0, removed: 0, changed: 0 };
      }

      let added = 0, removed = 0, changed = 0;

      const baseCovMap = new Map();
      coverageList.forEach(cov => {
        if (cov.use && !cov.is_union) {
          const key = cov.original_idx;
          if (!baseCovMap.has(key)) {
            baseCovMap.set(key, cov);
          }
        }
      });

      const compareCovMap = new Map();
      comparisonCoverageList.forEach(cov => {
        if (cov.use && !cov.is_union) {
          const key = cov.original_idx;
          if (!compareCovMap.has(key)) {
            compareCovMap.set(key, cov);
          }
        }
      });

      compareCovMap.forEach((cov, key) => {
        if (!baseCovMap.has(key)) added++;
      });

      baseCovMap.forEach((cov, key) => {
        if (!compareCovMap.has(key)) removed++;
      });

      baseCovMap.forEach((baseCov, key) => {
        const compareCov = compareCovMap.get(key);
        if (compareCov && Number(baseCov.value) !== Number(compareCov.value)) {
          changed++;
        }
      });

      return { added, removed, changed };
    });

    // 가격 차이
    const priceDiff = computed(() => {
      if (!compareMode.value || !comparisonSolution.value) return 0;
      return getComparisonSolutionPrice() - getSolutionPrice();
    });

    // ========================================
    // 캔버스 초기화 및 리사이즈
    // ========================================
    function initCanvas() {
      if (!feedbackCanvas.value || !feedbackMode.value) return;
      
      const canvas = feedbackCanvas.value;
      const container = canvas.parentElement;
      
      // 캔버스 크기 설정
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      
      canvasContext.value = canvas.getContext('2d');
      
      // 기존 어노테이션 로드 및 그리기
      loadExistingAnnotations();
      redrawAnnotations();
    }

    // 기존 어노테이션 로드 (props에서)
    function loadExistingAnnotations() {
      const existing = props.p_solution.existing_annotations || props.p_solution.annotations || [];
      
      if (existing && existing.length > 0) {
        // 문자열이면 파싱
        let parsed = existing;
        if (typeof existing === 'string') {
          try {
            parsed = JSON.parse(existing);
          } catch (e) {
            console.error('annotations 파싱 실패:', e);
            parsed = [];
          }
        }
        
        annotations.value = parsed;
      }
    }

    function handleResize() {
      if (feedbackMode.value) {
        nextTick(() => {
          initCanvas();
        });
      }
    }

    // ========================================
    // 비율 ↔ 절대 좌표 변환
    // ========================================

    // 저장된 좌표 → 현재 화면 크기에 맞게 변환 (렌더링용)
    function toAbsolutePoint(point, annotation = null) {
      const canvas = feedbackCanvas.value;
      if (!canvas) return point;
      
      // 원본 캔버스 크기 (저장 시점, 기본 1920x1080)
      const origWidth = annotation?.canvasWidth || 1920;
      const origHeight = annotation?.canvasHeight || 1080;
      
      // 현재 캔버스 / 원본 캔버스 비율
      const scaleX = canvas.width / origWidth;
      const scaleY = canvas.height / origHeight;
      
      return {
        x: point.x * scaleX,
        y: point.y * scaleY
      };
    }

    // 마우스/터치 이벤트에서 비율 좌표 얻기
    function getCanvasPoint(event) {
      if (!feedbackCanvas.value) return null;
      
      const canvas = feedbackCanvas.value;
      const rect = canvas.getBoundingClientRect();
      
      // 절대 좌표 반환 (픽셀값 그대로)
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    }

    // ========================================
    // 어노테이션 그리기
    // ========================================
    
    // 어노테이션 다시 그리기
    function redrawAnnotations() {
      if (!canvasContext.value || !feedbackCanvas.value) {
        return;
      }
      
      const ctx = canvasContext.value;
      const canvas = feedbackCanvas.value;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      annotations.value.forEach((annotation) => {
        drawAnnotation(ctx, annotation);
      });
    }

    // 단일 어노테이션 그리기
    function drawAnnotation(ctx, annotation) {
      ctx.strokeStyle = annotation.color;
      ctx.fillStyle = annotation.color;
      ctx.lineWidth = annotation.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 1;
      
      switch (annotation.type) {
        case 'pen':
          drawPath(ctx, annotation.points);
          break;
        case 'highlighter':
          ctx.globalAlpha = 0.3;
          ctx.lineWidth = annotation.size * 4;
          drawPath(ctx, annotation.points);
          ctx.globalAlpha = 1;
          break;
        case 'arrow':
          drawArrow(ctx, annotation.points, annotation.size);
          break;
        case 'rect':
          drawRect(ctx, annotation.points);
          break;
        case 'text':
          drawText(ctx, annotation);
          break;
      }
    }

    function drawPath(ctx, points) {
      if (!points || points.length < 2) return;
      
      ctx.beginPath();
      const start = toAbsolutePoint(points[0]);
      ctx.moveTo(start.x, start.y);
      
      for (let i = 1; i < points.length; i++) {
        const pt = toAbsolutePoint(points[i]);
        ctx.lineTo(pt.x, pt.y);
      }
      
      ctx.stroke();
    }

    function drawArrow(ctx, points, size) {
      if (!points || points.length < 2) return;
      
      const start = toAbsolutePoint(points[0]);
      const end = toAbsolutePoint(points[points.length - 1]);
      
      // 선 그리기
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      
      // 화살표 머리 그리기
      const angle = Math.atan2(end.y - start.y, end.x - start.x);
      const headLength = 15 + size * 2;
      
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(
        end.x - headLength * Math.cos(angle - Math.PI / 6),
        end.y - headLength * Math.sin(angle - Math.PI / 6)
      );
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(
        end.x - headLength * Math.cos(angle + Math.PI / 6),
        end.y - headLength * Math.sin(angle + Math.PI / 6)
      );
      ctx.stroke();
    }

    function drawRect(ctx, points) {
      if (!points || points.length < 2) return;
      
      const start = toAbsolutePoint(points[0]);
      const end = toAbsolutePoint(points[points.length - 1]);
      
      ctx.strokeRect(
        Math.min(start.x, end.x),
        Math.min(start.y, end.y),
        Math.abs(end.x - start.x),
        Math.abs(end.y - start.y)
      );
    }

    function drawText(ctx, annotation) {
      if (!annotation.text || !annotation.position) return;
      
      const pos = toAbsolutePoint(annotation.position);
      const fontSize = annotation.fontSize || 16;
      
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillText(annotation.text, pos.x, pos.y);
    }

    // ========================================
    // 드로잉 이벤트 핸들러
    // ========================================
    function startDrawing(event) {
      if (feedbackMode.value === 'view') return;
      if (currentTool.value === 'text') {
        handleTextTool(event);
        return;
      }
      
      isDrawing.value = true;
      const point = getCanvasPoint(event);
      if (point) {
        currentPath.value = [point];
        
        if (currentTool.value === 'eraser') {
          eraseAtPoint(point);
        }
      }
    }

    function draw(event) {
      if (!isDrawing.value || feedbackMode.value === 'view') return;
      
      const point = getCanvasPoint(event);
      if (!point) return;
      
      if (currentTool.value === 'eraser') {
        eraseAtPoint(point);
        return;
      }
      
      currentPath.value.push(point);
      
      // 실시간 프리뷰 그리기
      const ctx = canvasContext.value;
      if (!ctx) return;
      
      // 기존 어노테이션 다시 그리기
      redrawAnnotations();
      
      // 현재 그리는 중인 경로 그리기
      ctx.strokeStyle = currentColor.value;
      ctx.fillStyle = currentColor.value;
      ctx.lineWidth = currentSize.value;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (currentTool.value === 'highlighter') {
        ctx.globalAlpha = 0.3;
        ctx.lineWidth = currentSize.value * 4;
      }
      
      if (currentTool.value === 'pen' || currentTool.value === 'highlighter') {
        drawPath(ctx, currentPath.value);
      } else if (currentTool.value === 'arrow') {
        drawArrow(ctx, currentPath.value, currentSize.value);
      } else if (currentTool.value === 'rect') {
        drawRect(ctx, currentPath.value);
      }
      
      ctx.globalAlpha = 1;
    }

    function stopDrawing() {
      if (!isDrawing.value || currentTool.value === 'eraser') {
        isDrawing.value = false;
        return;
      }
      
      if (currentPath.value.length > 1) {
        // 어노테이션 저장
        annotations.value.push({
          type: currentTool.value,
          points: [...currentPath.value],
          color: currentColor.value,
          size: currentSize.value,
          timestamp: Date.now()
        });
      }
      
      isDrawing.value = false;
      currentPath.value = [];
      redrawAnnotations();
    }

    // 터치 이벤트 핸들러
    function handleTouchStart(event) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        startDrawing({ clientX: touch.clientX, clientY: touch.clientY });
      }
    }

    function handleTouchMove(event) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        draw({ clientX: touch.clientX, clientY: touch.clientY });
      }
    }

    // 텍스트 도구 처리
    function handleTextTool(event) {
      const point = getCanvasPoint(event);
      if (!point) return;
      
      const text = prompt('텍스트를 입력하세요:');
      if (text) {
        annotations.value.push({
          type: 'text',
          text: text,
          position: point,
          color: currentColor.value,
          fontSize: 14 + currentSize.value * 2,
          timestamp: Date.now()
        });
        redrawAnnotations();
      }
    }
    
    // 지우개
    function eraseAtPoint(point) {
      const canvas = feedbackCanvas.value;
      const eraseRadius = 20;
      
      annotations.value = annotations.value.filter(annotation => {
        const origWidth = annotation.canvasWidth || 1920;
        const origHeight = annotation.canvasHeight || 1080;
        const scaleX = canvas.width / origWidth;
        const scaleY = canvas.height / origHeight;
        
        if (annotation.type === 'text') {
          const scaledPos = {
            x: annotation.position.x * scaleX,
            y: annotation.position.y * scaleY
          };
          const dx = scaledPos.x - point.x;
          const dy = scaledPos.y - point.y;
          return Math.sqrt(dx * dx + dy * dy) > eraseRadius;
        }
        
        if (annotation.points) {
          return !annotation.points.some(p => {
            const scaledP = { x: p.x * scaleX, y: p.y * scaleY };
            const dx = scaledP.x - point.x;
            const dy = scaledP.y - point.y;
            return Math.sqrt(dx * dx + dy * dy) < eraseRadius;
          });
        }
        
        return true;
      });
      
      redrawAnnotations();
    }

    // 도구 선택
    function selectTool(toolId) {
      currentTool.value = toolId;
    }

    // 실행취소
    function undoAnnotation() {
      if (annotations.value.length > 0) {
        annotations.value.pop();
        redrawAnnotations();
      }
    }

    // 전체 지우기
    function clearAllAnnotations() {
      if (confirm('모든 어노테이션을 지우시겠습니까?')) {
        annotations.value = [];
        redrawAnnotations();
      }
    }

    // ========================================
    // 피드백 저장 및 완료
    // ========================================
    async function saveFeedbackDraft() {
      if (!feedbackIdx.value) return;
      
      const annotationsJson = JSON.stringify(annotations.value);
      
      const sql = `
        UPDATE IS_solution_feedback 
        SET annotations = '${annotationsJson.replace(/'/g, "\\'")}',
            comment = '${(feedbackComment.value || '').replace(/'/g, "\\'")}'
        WHERE idx = ${feedbackIdx.value}
      `;
      
      await sql_query(sql);
      alert('임시저장되었습니다.');
    }

    async function completeFeedback() {
      if (!feedbackIdx.value) return;
      
      if (!confirm('피드백을 완료하시겠습니까?\n완료 후에는 수정할 수 없습니다.')) {
        return;
      }
      
      const annotationsJson = JSON.stringify(annotations.value);
      
      // 피드백 테이블 업데이트
      const feedbackSql = `
        UPDATE IS_solution_feedback 
        SET status = 'complete',
            annotations = '${annotationsJson.replace(/'/g, "\\'")}',
            comment = '${(feedbackComment.value || '').replace(/'/g, "\\'")}',
            completed_at = NOW()
        WHERE idx = ${feedbackIdx.value}
      `;
      await sql_query(feedbackSql);
      
      // 솔루션 상태 업데이트
      const solutionSql = `
        UPDATE IS_solution 
        SET feedback_status = 'complete' 
        WHERE idx = ${solutionInfo.value.idx}
      `;
      await sql_query(solutionSql);
      
      alert('피드백이 완료되었습니다.');
      closeView();
    }

    // ========================================
    // 화면 크기 감지
    // ========================================
    const reportWindowSize = () => {
      halfLettering.value = window.innerWidth >= 1120;
    };

    // ========================================
    // 비교 모드 함수들
    // ========================================
    async function fetchAvailableSolutions() {
      try {
        const sql = `
          SELECT s.*, c.name as customer_name 
          FROM IS_solution s 
          LEFT JOIN customers c ON s.customer_idx = c.idx 
          WHERE s.mb_id = '${member_info.mb_id}' 
          ORDER BY s.updated_at DESC 
          LIMIT 50
        `;
        
        const result = await sql_query(sql);
        availableSolutions.value = Array.isArray(result) ? result : [];
      } catch (error) {
        console.error('솔루션 로드 실패:', error);
        availableSolutions.value = [];
      }
    }

    // 솔루션 피커 열기
    async function openSolutionPicker() {
      console.log('🔥 openSolutionPicker 호출됨!');
      console.log('로드 전 솔루션 수:', availableSolutions.value.length);
      
      // 항상 최신 데이터 로드
      await fetchAvailableSolutions();
      
      console.log('로드 후 솔루션 수:', availableSolutions.value.length);
      showSolutionPicker.value = true;
    }

    async function selectComparisonSolution(sol) {
      if (sol.idx === solutionInfo.value.idx) return;
      
      comparisonSolution.value = sol;
      showSolutionPicker.value = false;
      
      await loadComparisonData(sol.idx);
      compareMode.value = true;
    }

    async function loadComparisonData(solutionIdx) {
      const planSql = `SELECT * FROM IS_plan WHERE solution_id = '${solutionIdx}' ORDER BY number ASC`;
      const plans = await sql_query(planSql);
      comparisonPlanList.value = (plans || []).map(plan => ({
        idx: plan.idx,
        fake_idx: plan.idx,
        name: plan.name,
        pay: plan.pay,
        pay_type: plan.pay_type || 0,
        end: plan.end,
        end_type: plan.end_type || 0,
        price: plan.price,
        type: plan.type,
        type_id: plan.type_id || 0,
        company: plan.company,
        company_etc: plan.company_etc || '',
        joined_at: (plan.joined_at || '').slice(0, 10),
      }));

      comparisonCoverageList.length = 0;
      const originals = await sql_query(`SELECT * FROM IS_ex_coverage`);
      const originalMap = new Map((originals || []).map(o => [o.idx, o]));

      for (const plan of comparisonPlanList.value) {
        const covSql = `SELECT * FROM IS_coverage WHERE plan_id = ${plan.idx}`;
        const coverages = await sql_query(covSql);
        
        for (const cov of (coverages || [])) {
          const original = originalMap.get(cov.original);
          comparisonCoverageList.push({
            idx: cov.idx,
            name: cov.nick || original?.name || '',
            no: original?.no || 9999,
            short_name: cov.short_name || original?.short_name || '',
            long_name: cov.long_name || original?.long_name || '',
            chapter_idx: cov.chapter_id || original?.chapter_id,
            plan_idx: cov.plan_id,
            plan_fake_idx: plan.fake_idx,
            value: cov.value,
            use: cov.visible || false,
            original_idx: cov.original,
            is_union: cov?.is_union || 0,
            low_show: cov?.low_show || 0,
            style_bold: original?.style_bold || 0,
            style_important: original?.style_important || 0,
          });
        }
      }
    }

    function toggleCompareMode() {
      if (!comparisonSolution.value) {
        showSolutionPicker.value = true;
        return;
      }
      compareMode.value = !compareMode.value;
    }

    function getCoverageCompareStatus(coverage) {
      if (!compareMode.value) return '';
      
      const compareCov = comparisonCoverageList.find(
        c => c.original_idx === coverage.original_idx && c.use && !c.is_union
      );
      
      if (!compareCov) {
        return 'removed';
      }
      
      const baseVal = Number(coverage.value);
      const compareVal = Number(compareCov.value);
      
      if (baseVal !== compareVal) {
        return compareVal > baseVal ? 'changed-up' : 'changed-down';
      }
      
      return '';
    }

    function getComparisonCoverageStatus(coverage) {
      const baseCov = coverageList.find(
        c => c.original_idx === coverage.original_idx && c.use && !c.is_union
      );
      
      if (!baseCov) {
        return 'added';
      }
      
      const baseVal = Number(baseCov.value);
      const compareVal = Number(coverage.value);
      
      if (baseVal !== compareVal) {
        return compareVal > baseVal ? 'changed-up' : 'changed-down';
      }
      
      return '';
    }

    function getChapterDiff(chapter) {
      if (!compareMode.value) return { added: 0, removed: 0, changed: 0 };

      let added = 0, removed = 0, changed = 0;

      Object.values(chapter.list).forEach(child => {
        const baseCovs = coverageList.filter(
          c => c.chapter_idx == child.idx && c.use && !c.is_union
        );
        
        const compareCovs = comparisonCoverageList.filter(
          c => c.chapter_idx == child.idx && c.use && !c.is_union
        );

        const baseMap = new Map();
        baseCovs.forEach(cov => baseMap.set(cov.original_idx, cov));

        const compareMap = new Map();
        compareCovs.forEach(cov => compareMap.set(cov.original_idx, cov));

        compareMap.forEach((cov, key) => {
          if (!baseMap.has(key)) added++;
        });

        baseMap.forEach((cov, key) => {
          if (!compareMap.has(key)) removed++;
        });

        baseMap.forEach((baseCov, key) => {
          const compareCov = compareMap.get(key);
          if (compareCov && Number(baseCov.value) !== Number(compareCov.value)) {
            changed++;
          }
        });
      });

      return { added, removed, changed };
    }

    function returnComparisonCoverages(planFakeIdx, chapterIdx) {
      return comparisonCoverageList.filter(
        c => c.chapter_idx == chapterIdx && c.plan_fake_idx == planFakeIdx
      );
    }

    function getComparisonHiddenCount(planFakeIdx, chapterIdx) {
      return comparisonCoverageList.filter(c =>
        c.plan_fake_idx === planFakeIdx &&
        c.chapter_idx === chapterIdx &&
        c.use != 1 &&
        c.is_union == 0
      ).length;
    }

    function getComparisonHiddenCountByChapter(planFakeIdx, chapter) {
      let total = 0;
      Object.values(chapter.list).forEach(item => {
        total += comparisonCoverageList.filter(c =>
          c.plan_fake_idx == planFakeIdx &&
          c.chapter_idx == item.idx &&
          c.use != 1 &&
          c.is_union == 0
        ).length;
      });
      return total;
    }

    function getComparisonCovsInChapter(plan, chapter) {
      const covs = [];
      if (!plan || !chapter || !chapter.list) return covs;

      Object.values(chapter.list).forEach(item => {
        const coverages = returnComparisonCoverages(plan.fake_idx, item.idx) || [];
        coverages.forEach(cov => covs.push(cov));
      });

      return covs;
    }

    function getComparisonSolutionPrice() {
      let price = 0;
      comparisonPlanList.value.forEach(plan => {
        price += Number(plan.price);
      });
      return price;
    }

    // ========================================
    // 기존 함수들
    // ========================================
    async function fetchConCus() {
      const query = `SELECT * FROM customers WHERE idx = '${solutionInfo.value.customer_idx}'`;
      const res = await sql_query(query);
      connectedCustomer.value = res?.[0] || null;
    }

    async function fetchChapter() {
      const rootSql = `SELECT * FROM IS_chapter WHERE (parent IS NULL OR parent = 0) AND status = 1 ORDER BY no ASC`;
      const rootChapters = await sql_query(rootSql);

      for (const chapter of (rootChapters || [])) {
        chapterList.value[chapter.en_name] = {
          idx: chapter.idx,
          no: chapter.no,
          name: chapter.name,
          delegate: chapter.delegate,
          show_type: chapter.show_type || 0,
          fold: true,
          is_oth: chapter.is_oth || 0,
          list: {}
        };
      }

      const childSql = `SELECT * FROM IS_chapter WHERE parent IS NOT NULL AND parent != 0 AND status = 1 ORDER BY no ASC`;
      const childChapters = await sql_query(childSql);

      for (const chapter of (childChapters || [])) {
        const parentObj = (rootChapters || []).find(r => r.idx === chapter.parent);
        if (!parentObj || !chapterList.value[parentObj.en_name]) continue;

        chapterList.value[parentObj.en_name].list[chapter.en_name] = {
          name: chapter.name,
          idx: chapter.idx,
          delegate: chapter.delegate,
          show_type: chapter.show_type || 0,
          is_oth: chapter.is_oth || 0,
          no: chapter.no,
        };
      }
    }

    async function fetchPlans() {
      const sql = `SELECT * FROM IS_plan WHERE solution_id = '${solutionInfo.value.idx}' ORDER BY number ASC LIMIT 0, 30`;
      const result = await sql_query(sql);
      
      for (const plan of (result || [])) {
        let s_plan = reactive({
          no: plan.number,
          idx: plan.idx,
          fake_idx: plan.idx || 0,
          name: plan.name,
          pay: plan.pay,
          pay_type: plan.pay_type || 0,
          end: plan.end,
          end_type: plan.end_type || 0,
          price: plan.price,
          type: plan.type,
          type_id: plan.type_id || 0,
          sub_type: plan.sub_type,
          joined_at: (plan.joined_at || '').slice(0, 10),
          company: plan.company,
          company_etc: plan.company_etc || '',
          coverages: {},
          renewable: plan.renewable || 0,
          remarks: plan.remarks || '',
          worth: plan.worth || 0,
          return_worth: plan.return_worth || 0,
        });

        solution.value.plan_list.push(s_plan);
      }
      
      await fetchCoverages();
    }

    async function fetchCoverages() {
      const plans = solution.value.plan_list;
      const originals = await sql_query(`SELECT * FROM IS_ex_coverage`);
      const originalMap = new Map((originals || []).map(o => [o.idx, o]));

      const planTasks = plans.map(async (plan) => {
        const sql = `SELECT * FROM IS_coverage WHERE plan_id = ${plan.idx} ORDER BY idx DESC`;
        const coverages = await sql_query(sql);
        return { plan, coverages: coverages || [] };
      });

      const allPlanCoverage = await Promise.all(planTasks);

      for (const { plan, coverages } of allPlanCoverage) {
        for (const coverage of coverages) {
          const original = originalMap.get(coverage.original);

          coverageList.push({
            idx: coverage.idx,
            name: coverage.nick || original?.name || '',
            no: original?.no || 9999,
            short_name: coverage.short_name || original?.short_name || '',
            long_name: coverage.long_name || original?.long_name || '',
            chapter_idx: coverage.chapter_id || original?.chapter_id,
            plan_idx: coverage.plan_id,
            plan_fake_idx: plan.fake_idx,
            value: coverage.value,
            parent: coverage.parent || '',
            fold: false,
            use: coverage.visible || false,
            calculable: coverage.calculable || false,
            visible: true,
            style_bold: original?.style_bold || 0,
            style_important: original?.style_important || 0,
            original_idx: coverage.original,
            is_union: coverage?.is_union || 0,
            low_show: coverage?.low_show || 0,
          });
        }
      }

      addCoverageToSolution();
      await mergePlansCoverages();
    }

    async function addCoverageToSolution() {
      solution.value.plan_list.forEach(plan => {
        Object.values(chapterList.value).forEach(chapter => {
          if (!plan.coverages[chapter.idx]) {
            let a = [];
            a['name'] = `${chapter.name}`;
            Object.values(chapter.list).forEach(child => {
              const coverages = returnCoverages(plan.idx, child.idx);
              a[child.idx] = { idx: child.idx, name: child.name, list: coverages };
            });
            plan.coverages[chapter.idx] = a;
          }

          const coverages = returnCoverages(plan.idx, chapter.idx);
          if (coverages.length > 0) {
            plan.coverages[chapter.idx] = coverages;
          }
        });
      });
    }

    function foldOpenByKey(key) {
      const current = chapterList.value[key];
      if (!current) return;

      const isFolded = current.fold;
      Object.values(chapterList.value).forEach(ch => { ch.fold = true; });
      current.fold = !isFolded;
    }

    function returnCoverages(planFakeIdx, chapterIdx) {
      return coverageList.filter(
        c => c.chapter_idx == chapterIdx && c.plan_fake_idx == planFakeIdx
      );
    }

    function flattenCoverageTree(list) {
      const map = new Map();
      const roots = [];

      list.forEach(item => {
        item.children = [];
        map.set(item.idx, item);
      });

      map.forEach(item => {
        if (item.parent && !isNaN(item.parent)) {
          const parentId = Number(item.parent);
          if (parentId === item.idx) {
            roots.push(item);
            return;
          }

          const parent = map.get(parentId);
          if (parent) {
            parent.children.push(item);
          } else {
            roots.push(item);
          }
        } else {
          roots.push(item);
        }
      });

      const visited = new Set();

      function assignDepth(node, depth = 0) {
        if (visited.has(node.idx)) return;
        visited.add(node.idx);
        node._depth = depth;
        node.children.forEach(child => assignDepth(child, depth + 1));
      }

      roots.forEach(root => assignDepth(root));
      return roots;
    }

    function orderedCoverages(editPlanIdx, editingChapterIdx) {
      return flattenCoverageTree(
        returnCoverages(editPlanIdx, editingChapterIdx)
      );
    }

    function genderChoser(code) {
      if (!code || code == 0) return '무성';
      return code % 2 === 0 ? '여성' : '남성';
    }

    function formatMoney(val) {
      if (val === null || val === undefined) return '-';
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    function getHiddenCount(planFakeIdx, chapterIdx) {
      return coverageList.filter(c =>
        c.plan_fake_idx === planFakeIdx &&
        c.chapter_idx === chapterIdx &&
        c.use != 1 &&
        c.is_union == 0
      ).length;
    }

    function getHiddenCountByChapter(planFakeIdx, chapter) {
      let total = 0;
      Object.values(chapter.list).forEach(item => {
        total += coverageList.filter(c =>
          c.plan_fake_idx == planFakeIdx &&
          c.chapter_idx == item.idx &&
          c.use != 1 &&
          c.is_union == 0
        ).length;
      });
      return total;
    }

    function getCovsInChapter(plan, chapter) {
      const covs = [];
      if (!plan || !chapter || !chapter.list) return covs;

      Object.values(chapter.list).forEach(item => {
        const coverages = returnCoverages(plan.fake_idx, item.idx) || [];
        coverages.forEach(cov => covs.push(cov));
      });

      return covs;
    }

    function getPayTypeLabel(code) {
      return { 0: "년납", 1: "세납", 2: "일시납", 3: "기타(그외)" }[code] || "-";
    }

    function getEndTypeLabel(code) {
      return { 0: "세만기", 1: "년만기", 2: "종신", 3: "기타(그외)" }[code] || "-";
    }

    function getPayTypeShort(code) {
      return { 0: "년", 1: "세", 2: "일시", 3: "" }[code] || "";
    }

    function getEndTypeShort(code) {
      return { 0: "세", 1: "년", 2: "종신", 3: "" }[code] || "";
    }

    function getTypeTypeLabel(code) {
      const map = {
        0: "직접입력", 1: "종합보장", 2: "건강/상해", 3: "단독실손",
        4: "종신/정기", 5: "연금/저축", 6: "회사/법인", 7: "실버/상속",
        8: "자녀/태아", 9: "운전자", 10: "치아보험", 11: "골프/레저", 12: "주택/건물"
      };
      return map[code] || "기타";
    }

    function getSolutionPrice() {
      let price = 0;
      solution.value.plan_list.forEach(plan => {
        price += Number(plan.price);
      });
      return price;
    }

    function getDelegateValues(planFakeIdx, chapterKey) {
      const chapterEnName = chapterMapping[chapterKey];
      if (!chapterEnName) return '';
      
      let values = [];

      if (['cancer', 'brain', 'heart'].includes(chapterKey)) {
        const parentChapter = chapterList.value['three_major'];
        if (parentChapter && parentChapter.list[chapterEnName]) {
          const childChapter = parentChapter.list[chapterEnName];
          const childIdx = childChapter.idx;
          
          const coverages = returnCoverages(planFakeIdx, childIdx);
          coverages
            .filter(c => c.use && !c.is_union && !c.low_show)
            .sort((a, b) => a.no - b.no)
            .forEach(cov => {
              const shortName = shortenCovName(cov.short_name || cov.name) || cov.name?.slice(0, 4) || '';
              const classes = [cov.style_bold ? 'bold' : '', cov.style_important ? 'important' : ''].filter(Boolean).join(' ');
              values.push(classes ? `<span class="${classes}">${shortName}: ${formatMoneyToKor(cov.value)}</span>` : `${shortName}: ${formatMoneyToKor(cov.value)}`);
            });
        }
      } else {
        const chapter = chapterList.value[chapterEnName];
        if (!chapter) return '';

        Object.values(chapter.list).forEach(child => {
          const coverages = returnCoverages(planFakeIdx, child.idx);
          coverages
            .filter(c => c.use && !c.is_union && !c.low_show)
            .sort((a, b) => a.no - b.no)
            .forEach(cov => {
              const shortName = shortenCovName(cov.short_name || cov.name) || cov.name?.slice(0, 4) || '';
              const classes = [cov.style_bold ? 'bold' : '', cov.style_important ? 'important' : ''].filter(Boolean).join(' ');
              values.push(classes ? `<span class="${classes}">${shortName}: ${formatMoneyToKor(cov.value)}</span>` : `${shortName}: ${formatMoneyToKor(cov.value)}`);
            });
        });
      }

      return values.length > 0 ? values.join('<br>') : '';
    }

    function printSolution(mode = 'card') {
      printMode.value = mode;
      
      setTimeout(() => {
        const prevTitle = document.title;
        document.title = solutionInfo.value.name;
        window.onafterprint = () => {
          document.title = prevTitle;
        };

        window.print();
      }, 100);
    }

    function openFooter() {
      const footer = document.querySelector('.footer');
      if (footer) {
        footer.classList.toggle('open');
        footer.classList.toggle('closed');
      }
    }

    function closeView() {
      props.closeFunction(false);
    }

    async function mergePlansCoverages() {
      let plans = solution.value.plan_list;
      const merged = {};
      
      plans.forEach(plan => {
        Object.entries(plan.coverages).forEach(([chapterIdx, chapter]) => {
          if (!merged[chapterIdx]) { 
            merged[chapterIdx] = { name: chapter.name, items: {} }; 
          }
          
          chapter.forEach?.((mid_chapter) => {
            if (mid_chapter && mid_chapter.name && mid_chapter.idx !== undefined) {
              const key = mid_chapter.name;
              
              if (!merged[chapterIdx].items[key]) { 
                merged[chapterIdx].items[key] = { 
                  idx: mid_chapter.idx, 
                  name: mid_chapter.name, 
                  list: [] 
                }; 
              }
              
              let orderedList = orderedCoverages(plan.fake_idx, mid_chapter.idx);

              if (orderedList && orderedList.length > 0) {
                orderedList.forEach(coverage => {
                  if (!coverage.is_union) return;
                  merged[chapterIdx].items[key].list.push({...coverage});
                });
              }
            }
          });
        });
      });
      
      resultCovsByChapter.value = merged;
    }

    function getResultFromUnionsChild(union) {
      if (!union || !Array.isArray(union.children)) return 0;
      let result = 0;
      for (const child of union.children) {
        if (child.calculable === 1) {
          result += Number(child.value || 0);
        }
      }
      return result;
    }

    function formatMoneyToKor(money) {
      if (money >= 10000) {
        const val = money / 10000;
        return `${trimNumber(val)}억`;
      } else if (money >= 1000) {
        const val = money / 1000;
        return `${trimNumber(val)}천`;
      } else if (money >= 100) {
        const val = money / 100;
        return `${trimNumber(val)}백`;
      } else {
        return `${money}만`;
      }
    }

    function trimNumber(num) {
      if (Number.isInteger(num)) return num.toString();
      
      let str = num.toString();
      
      if (str.length <= 4) return str;
      
      str = str.slice(0, 4);
      
      if (str.endsWith('.')) {
        str = str.slice(0, -1);
      }
      
      return str;
    }

    function shortenCovName(covName) {
      let result = covName;
      
      const replaceMap = {
        '상해사망': '상사',
        '질병사망': '질사',
        '상해후유': '상휴',
        '질병후유': '질휴',
        '일반암': '일반',
        '유사암': '유사',
        '고액암': '고액',
        '상수술': '상수',
        '질수술': '질수',
        '암수술': '암수',
        '뇌혈관': '뇌혈',
        '허혈성': '허혈',
        '상해입원': '상입',
        '질병입원': '질입',
        '간병사용상해': '간병사용(상)',
        '간병사용질병': '간병사용(질)',
        '실손(4.0)': '실손',
        '양성종양진단': '양성종양',
      };
      
      for (const [from, to] of Object.entries(replaceMap)) {
        result = result.replace(from, to);
      }
      
      return result.trim() || covName;
    }

    // ========================================
    // 라이프사이클
    // ========================================
    onMounted(async () => {
      await fetchConCus();
      await fetchChapter();
      await fetchPlans();
      
      window.addEventListener('resize', reportWindowSize);
      window.addEventListener('resize', handleResize);
      
      // 피드백 모드면 캔버스 초기화
      if (feedbackMode.value) {
        nextTick(() => {
          setTimeout(() => {
            initCanvas();
          }, 100);
        });
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', reportWindowSize);
      window.removeEventListener('resize', handleResize);
    });

    // 피드백 모드 변경 감지
    watch(feedbackMode, (newVal) => {
      if (newVal) {
        nextTick(() => {
          setTimeout(() => {
            initCanvas();
          }, 100);
        });
      }
    });

    return {
      solutionInfo,
      chapterList,
      solution,
      connectedCustomer,
      basic_profile_url,
      halfLettering,
      resultCovsByChapter,
      member_info,
      printMode,
      chartContainer,
      // 비교 모드
      compareMode,
      showSolutionPicker,
      solutionSearchQuery,
      availableSolutions,
      filteredSolutions,
      comparisonSolution,
      comparisonPlanList,
      comparisonCoverageList,
      comparisonStats,
      priceDiff,
      toggleCompareMode,
      selectComparisonSolution,
      getCoverageCompareStatus,
      getComparisonCoverageStatus,
      getChapterDiff,
      getComparisonSolutionPrice,
      returnComparisonCoverages,
      getComparisonHiddenCount,
      getComparisonHiddenCountByChapter,
      getComparisonCovsInChapter,
      openSolutionPicker,
      // 피드백 시스템
      feedbackMode,
      feedbackIdx,
      feedbackCanvas,
      feedbackComment,
      commentPanelExpanded,
      isDrawing,
      currentTool,
      currentColor,
      currentSize,
      annotations,
      feedbackTools,
      toolColors,
      brushSizes,
      startDrawing,
      draw,
      stopDrawing,
      handleTouchStart,
      handleTouchMove,
      selectTool,
      undoAnnotation,
      clearAllAnnotations,
      saveFeedbackDraft,
      completeFeedback,
      // 기존 함수들
      getInsuCompanyInfo,
      calculateInsuAge,
      genderChoser,
      formatMoney,
      foldOpenByKey,
      returnCoverages,
      getHiddenCount,
      getHiddenCountByChapter,
      getCovsInChapter,
      getPayTypeLabel,
      getEndTypeLabel,
      getPayTypeShort,
      getEndTypeShort,
      getTypeTypeLabel,
      getSolutionPrice,
      getDelegateValues,
      printSolution,
      openFooter,
      closeView,
      getResultFromUnionsChild,
    };
  },
}
</script>

<style scoped>
/* ========================================
   기본 스타일
======================================== */
#module-CA-view {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 999999;
  width: 100%;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 24px;
  background-color: var(--color-background);
}

#module-CA-view.CA_A {
  --tranditanl-color: var(--color-ca-a);
  --tranditanl-color-light: var(--color-ca-a-light);
}

#module-CA-view.CA_B {
  --tranditanl-color: var(--color-ca-b);
  --tranditanl-color-light: var(--color-ca-b-light);
}

#module-CA-view.compare-mode {
  gap: 16px;
}

/* ========================================
   피드백 캔버스 시스템
======================================== */
.feedback-canvas-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  pointer-events: none;
}

.feedback-canvas-container.view-only {
  pointer-events: none;
}

.feedback-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  cursor: crosshair;
}

.feedback-canvas-container.view-only .feedback-canvas {
  pointer-events: none;
  cursor: default;
}

.feedback-toolbar {
  position: fixed;
  top: 80px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  z-index: 10001;
  pointer-events: auto;
}

.toolbar-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toolbar-section.tools,
.toolbar-section.actions {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
}

.toolbar-section.colors {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.toolbar-section.sizes {
  flex-direction: row;
  justify-content: center;
  gap: 6px;
}

.toolbar-divider {
  width: 100%;
  height: 2px;
  background: var(--color-text-primary);
  margin: 4px 0;
}

.tool-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
  font-size: 18px;
  color: var(--color-text-primary);
}

.tool-btn:hover {
  background: var(--color-surface-reverse);
}

.tool-btn.active {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.color-btn {
  width: 28px;
  height: 28px;
  border: 2px solid var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
}

.color-btn:hover {
  transform: scale(1.1);
}

.color-btn.active {
  outline: 3px solid var(--color-text-primary);
  outline-offset: 2px;
}

.size-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
}

.size-btn:hover {
  background: var(--color-surface-reverse);
}

.size-btn.active {
  background: var(--color-text-primary);
}

.size-btn span {
  background: var(--color-text-primary);
  border-radius: 50%;
}

.size-btn.active span {
  background: var(--color-background);
}

.action-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
  font-size: 18px;
  color: var(--color-text-primary);
}

.action-btn:hover {
  background: var(--color-surface-reverse);
}

.action-btn.clear:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: white;
}

.feedback-comment-panel {
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 320px;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  z-index: 10001;
  pointer-events: auto;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--color-text-primary);
  color: var(--color-background);
  cursor: pointer;
  font-weight: 700;
  font-size: var(--font-size-sm);
}

.panel-header span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-header i:last-child {
  font-size: 12px;
}

.panel-body {
  padding: 16px;
}

.panel-body textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 2px solid var(--color-text-primary);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.panel-body textarea:focus {
  outline: none;
  background: var(--color-surface-reverse);
}

.panel-body textarea::placeholder {
  color: var(--color-text-secondary);
}

.comment-view {
  padding: 12px;
  background: var(--color-surface-reverse);
  border: 2px solid var(--color-text-primary);
  min-height: 100px;
}

.comment-view p {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-wrap;
}

.comment-view .no-comment {
  color: var(--color-text-secondary);
  font-style: italic;
}

.feedback-actions {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 12px;
  z-index: 10001;
  pointer-events: auto;
}

.feedback-save-btn,
.feedback-complete-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  font-size: var(--font-size-base);
  font-weight: 700;
  border: 2px solid var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
}

.feedback-save-btn {
  background: var(--color-background);
  color: var(--color-text-primary);
}

.feedback-save-btn:hover {
  background: var(--color-surface-reverse);
}

.feedback-complete-btn {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.feedback-complete-btn:hover {
  background: #22c55e;
  border-color: #22c55e;
}

.feedback-view-actions {
  position: fixed;
  top: 80px;
  left: 20px;
  z-index: 10001;
  pointer-events: auto;
}

.feedback-info-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-text-primary);
  color: var(--color-background);
  font-size: var(--font-size-sm);
  font-weight: 700;
}

.feedback-mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f59e0b;
  color: white;
  font-size: var(--font-size-sm);
  font-weight: 700;
  margin-left: 12px;
}

.footer.feedback-active {
  z-index: 9999;
}

/* ========================================
   헤더 스타일
======================================== */
.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  color: var(--color-text-primary);
  padding: 16px;
  box-sizing: border-box;
}

.main-header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.main-header > div {
  display: flex;
  align-items: center;
  gap: 12px;
}

.main-header img {
  width: 36px;
  height: 36px;
}

.main-header h2 {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 48px;
  flex-wrap: wrap;
  gap: 12px;
}

.header > div {
  display: flex;
  align-items: center;
  gap: 16px;
  width: fit-content;
  height: 100%;
}

.header > div.left {
  flex-wrap: wrap;
}

.header > div.right {
  flex-wrap: wrap;
}

.header > div > div {
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
  border: 1px solid var(--color-text-secondary);
  border-radius: 6px;
  gap: 6px;
  font-size: var(--font-size-md);
  font-weight: 700;
}

.header > div > div > img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.header > div > button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: 100%;
  padding: 0 24px;
  background: none;
  border: 1px solid var(--tranditanl-color-light);
  color: var(--color-text-primary);
  border-radius: 6px;
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  gap: 6px;
}

/* ========================================
   비교 모드 토글 버튼
======================================== */
.compare-toggle {
  display: flex;
  align-items: center;
  gap: 0;
  background: none;
  height: 44px;
}

.compare-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-surface-reverse);
  border: 2px solid var(--color-text-primary);
  border-right: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.compare-btn:hover:not(:disabled) {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.compare-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.compare-toggle.active .compare-btn {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.compare-btn i {
  font-size: 16px;
}

.select-compare-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  max-width: 160px;
}

.select-compare-btn:hover {
  background: var(--color-surface-reverse);
}

.select-compare-btn .compare-target {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
}

.select-compare-btn i {
  font-size: 12px;
}

/* ========================================
   솔루션 선택 다이얼로그
======================================== */
.solution-picker-overlay {
  position: absolute;
  inset: 0;
  z-index: 9999999;  /* 999999보다 높게! */
  left:0;
  top:0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.solution-picker-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
}

.solution-picker-dialog {
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: 70vh;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 2px solid var(--color-text-primary);
}

.picker-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.picker-header .close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 2px solid var(--color-text-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s;
}

.picker-header .close-btn:hover {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.picker-search {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px;
  padding: 10px 14px;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
}

.picker-search:focus-within {
  background: var(--color-surface-reverse);
}

.picker-search i {
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
}

.picker-search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
}

.picker-search input::placeholder {
  color: var(--color-text-secondary);
}

.picker-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.picker-list::-webkit-scrollbar {
  width: 4px;
}

.picker-list::-webkit-scrollbar-track {
  background: var(--color-surface-reverse);
}

.picker-list::-webkit-scrollbar-thumb {
  background: var(--color-text-primary);
}

.picker-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: var(--color-background);
  border: 2px solid var(--color-surface-reverse);
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.picker-item:hover {
  border-color: var(--color-text-primary);
  background: var(--color-surface-reverse);
}

.picker-item.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.picker-item.disabled:hover {
  border-color: var(--color-surface-reverse);
  background: var(--color-background);
}

.picker-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.picker-item-info .sol-name {
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-item-info .sol-customer {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.picker-item-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.picker-item-meta .sol-type {
  padding: 4px 8px;
  background: var(--color-text-primary);
  color: var(--color-background);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.picker-item-meta .sol-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.picker-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

/* ========================================
   비교 모드 Sticky 바
======================================== */
.compare-sticky-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  margin: 0 0 16px 0;
}

.compare-bar-content {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px 16px;
  background: var(--color-background);
  border: 2px solid var(--color-text-primary);
}

.compare-bar-sides {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.bar-side {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  min-width: 0;
  flex: 1;
  max-width: 200px;
}

.bar-side.base {
  background: var(--tranditanl-color);
}

.bar-side.target {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.side-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.side-name {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-side.target .side-name,
.bar-side.target .side-label {
  color: var(--color-background);
}

.side-price {
  font-size: var(--font-size-sm);
  font-weight: 700;
  white-space: nowrap;
}

.bar-side.target .side-price {
  color: var(--color-background);
}

.side-price small {
  font-size: 10px;
}

.bar-vs {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--color-text-primary);
  font-size: 14px;
  flex-shrink: 0;
}

.compare-bar-stats {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-left: 2px solid var(--color-text-primary);
}

.bar-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: var(--font-size-sm);
  font-weight: 700;
}

.bar-stat.added {
  background: #22c55e;
  color: white;
}

.bar-stat.removed {
  background: #ef4444;
  color: white;
}

.bar-stat.changed {
  background: #f59e0b;
  color: white;
}

.bar-stat i {
  font-size: 12px;
}

.compare-bar-diff {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: var(--font-size-base);
  font-weight: 700;
  white-space: nowrap;
  border-left: 2px solid var(--color-text-primary);
}

.compare-bar-diff.increase {
  background: #ef4444;
  color: white;
}

.compare-bar-diff.decrease {
  background: #22c55e;
  color: white;
}

.compare-bar-diff.same {
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.compare-bar-diff i {
  font-size: 14px;
}

.compare-bar-legend {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 2px solid var(--color-text-primary);
}

.legend-dot {
  width: 12px;
  height: 12px;
  cursor: help;
}

.legend-dot.added { background: #22c55e; }
.legend-dot.removed { background: #ef4444; }
.legend-dot.changed-up { background: #3b82f6; }
.legend-dot.changed-down { background: #f59e0b; }

@media (max-width: 900px) {
  .compare-bar-content {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .compare-bar-sides {
    order: 1;
    width: 100%;
    flex: none;
  }
  
  .bar-side {
    max-width: none;
  }
  
  .compare-bar-stats {
    order: 2;
    border: none;
    padding: 0;
  }
  
  .compare-bar-diff {
    order: 3;
    border: none;
  }
  
  .compare-bar-legend {
    order: 4;
    border: none;
    padding: 0;
  }
}

/* ========================================
   챕터 헤더 변경 배지
======================================== */
.chapter-diff-badge {
  display: inline-flex;
  gap: 4px;
  margin-left: 8px;
  font-size: 11px;
}

.chapter-diff-badge span {
  padding: 2px 6px;
  font-weight: 700;
}

.chapter-diff-badge .added {
  background: #22c55e;
  color: white;
}

.chapter-diff-badge .removed {
  background: #ef4444;
  color: white;
}

.chapter-diff-badge .changed {
  background: #f59e0b;
  color: white;
}

/* ========================================
   비교 모드 보장 항목 스타일
======================================== */
.chart > .plan > .coverage-list > .coverage > .list > ul > li.added {
  background: #22c55e !important;
  color: white !important;
  padding: 4px 8px;
  margin: 2px 0;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.added > span {
  color: white !important;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.removed {
  background: #ef4444 !important;
  color: white !important;
  padding: 4px 8px;
  margin: 2px 0;
  opacity: 0.6;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.removed > span {
  color: white !important;
  text-decoration: line-through;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.changed-up {
  background: #3b82f6 !important;
  color: white !important;
  padding: 4px 8px;
  margin: 2px 0;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.changed-up > span {
  color: white !important;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.changed-down {
  background: #f59e0b !important;
  color: white !important;
  padding: 4px 8px;
  margin: 2px 0;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.changed-down > span {
  color: white !important;
}

.diff-icon {
  font-size: 12px;
  margin-left: 4px;
  vertical-align: middle;
  color: white !important;
}

/* ========================================
   비교 구분선
======================================== */
.compare-divider {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 24px 0;
  margin: 0;
}

.compare-divider::before,
.compare-divider::after {
  content: '';
  flex: 1;
  height: 2px;
  background: var(--color-text-primary);
}

.compare-divider span {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: var(--color-text-primary);
  color: var(--color-background);
  font-size: var(--font-size-sm);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
}

/* ========================================
   비교 플랜 스타일
======================================== */
.plan.compare-plan {
  border: 2px solid var(--color-text-primary);
  position: relative;
}

.plan.compare-plan::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  background: var(--color-text-primary);
}

.plan.compare-plan > .plan-info {
  padding-left: 20px;
}

.plan-label {
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.base-label {
  background: var(--tranditanl-color);
  color: var(--color-text-primary);
}

.compare-label {
  background: var(--color-text-primary);
  color: var(--color-background);
}

.price-up {
  color: #ef4444 !important;
}

.price-down {
  color: #22c55e !important;
}

/* ========================================
   애니메이션
======================================== */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .solution-picker-dialog,
.modal-leave-to .solution-picker-dialog {
  transform: translateY(8px);
}

/* ========================================
   기존 스타일 (contents, chart, footer 등)
======================================== */
#etc-notice-sp {
  display: none;
  font-size: var(--font-size-md);
}

.contents {
  width: 100%;
  min-height: fit-content;
  padding: 0;
  margin: 0;
  display: flex;
  background-color: var(--tranditanl-color);
  overflow: hidden;
  border-radius: 12px;
}

.contents > .chapter {
  width: 100%;
  height: 80px;
  display: flex;
  flex: 3.2;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-right: 1px solid var(--tranditanl-color-light);
  background-color: var(--tranditanl-color-light);
}

.contents > .chapter.wide {
  flex: 3.6 !important;
}

.contents > .chapter:last-child {
  border-right: none;
}

.contents > .chapter.fold {
  flex: 1 !important;
  background-color: var(--tranditanl-color);
}

.contents > .chapter h2 {
  width: 100%;
  height: 100%;
  flex: 1.5;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--font-size-lg) !important;
  font-weight: var(--font-weight-bold);
  margin: 0;
  padding: 0px;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
}

.contents > .chapter h2:hover {
  background-color: var(--tranditanl-color-light);
}

.contents > .chapter > ul {
  width: 100%;
  height: 100%;
  flex: 1;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  border-top: 1px solid var(--tranditanl-color-light);
}

.contents > .chapter > ul > li {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  border-right: 1px solid var(--tranditanl-color-light);
}

.contents > .chapter > ul > li > span {
  font-size: 12px !important;
}

.contents > .chapter > ul > li:last-child {
  border-right: none;
}

.chart {
  width: 100%;
  height: 100%;
  max-height: 76%;
  padding: 0 0 200px 0;
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

#module-CA-view.compare-mode .chart {
  padding-top: 8px;
}

.chart::-webkit-scrollbar {
  display: none;
}

.chart > .plan {
  width: 100%;
  min-height: fit-content;
  height: fit-content;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: var(--tranditanl-color);
}

.chart > .plan > .plan-info {
  width: 100%;
  height: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--tranditanl-color-light);
}

.chart > .plan > .plan-info > ul {
  height: 100%;
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 12px;
}

.chart > .plan > .plan-info > ul > li {
  width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart > .plan > .plan-info > ul > li > img {
  width: 48px;
  height: 48px;
  border-radius: 6px;
}

.chart > .plan > .plan-info > ul > li h2 {
  font-size: var(--font-size-md);
}

.chart > .plan > .plan-info > ul > li span {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-extralight);
  color: var(--color-text-primary);
}

.chart > .plan > .coverage-list {
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  border: 1px solid var(---tranditanl-color-light);
  border-radius: 0px 0px 16px 16px;
  overflow: hidden;
}

.chart > .plan > .coverage-list .none {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0.6;
  font-size: var(--font-size-base);
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart > .plan > .coverage-list > .coverage {
  width: 100%;
  display: flex;
  flex: 3.2;
}

.chart > .plan > .coverage-list > .coverage.fold {
  flex: 1 !important;
}

.chart > .plan > .coverage-list > .coverage.wide {
  flex: 3.6;
}

.chart > .plan > .coverage-list > .coverage > .list {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 6px;
  box-sizing: border-box;
  border-right: 1px solid var(--tranditanl-color-light);
  padding-bottom: 36px;
}

.chart > .plan > .coverage-list > .coverage > .list.folder {
  background: var(--tranditanl-color-light);
}

.chart > .plan > .coverage-list > .coverage:last-child > .list:last-child {
  border-right: none;
}

.chart > .plan > .coverage-list > .coverage > .list > .other {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: var(--font-size-2xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-text-primary);
  padding: 6px;
  box-sizing: border-box;
  font-weight: 400 !important;
  opacity: 0.4;
}

.chart > .plan > .coverage-list > .coverage > .list > .other > span {
  font-size: var(--font-size-base);
}

.chart > .plan > .coverage-list > .coverage > .list > ul {
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 6px;
  flex-direction: column;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-direction: row;
  box-sizing: border-box;
  border-radius: 16px;
  font-size: var(--font-size-md);
  text-align: start;
}

@media (max-width: 1024px) or (max-height: 800px) {
  .chart > .plan > .coverage-list > .coverage > .list > ul > li {
    flex-direction: column;
    align-items: start;
  }
  .chart > .plan > .coverage-list > .coverage > .list > ul {
    gap: 16px;
  }
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li > span {
  font-size: var(--font-size-base);
  overflow: hidden;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.important {
  color: var(--color-text-primary);
  opacity: 0.5;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li.bold {
  font-weight: 1000;
}

.chart > .plan > .coverage-list > .coverage > .list > ul > li > span:first-child {
  flex: 1;
  display: flex;
  justify-content: start;
  align-items: center;
}

.excel-table {
  display: none;
}

.footer {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: fit-content;
  max-height: 70px;
  padding-bottom: 50px;
  box-sizing: border-box;
  background-color: var(--color-background);
  box-shadow: 0 -2px 4px var(--tranditanl-color-light);
  border-radius: 24px 24px 0 0;
  transition: all 0.2s ease-in-out;
}

.footer.closed {
  left: 0;
  bottom: 0;
}

.footer.open {
  left: 0;
  bottom: 0;
  height: fit-content;
  max-height: 500px;
}

.footer > .open_btn {
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.footer > .open_btn > span {
  width: 40px;
  height: 4px;
  background-color: var(--color-text-primary);
  border-radius: 2px;
  transition: all 0.2s ease-in-out;
}

.footer > .short {
  width: 100%;
  height: 36px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 16px;
  box-sizing: border-box;
}

.footer > .short > ul {
  width: fit-content;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 0;
  margin: 0;
}

.footer > .short > ul > li {
  width: fit-content;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--font-size-md);
  gap: 6px;
}

.footer > .short > ul > li > span {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
}

.footer > .short > ul > li > h2 {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  padding: 0;
  margin: 0;
}

.footer > ul {
  width: 100%;
  height: fit-content;
  display: flex;
  padding: 16px;
  box-sizing: border-box;
  list-style: none;
  margin: 0;
}

.footer > ul > li {
  width: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  font-size: var(--font-size-md);
  gap: 6px;
  border-right: 1px solid var(--tranditanl-color-light);
  padding: 0px 12px;
  box-sizing: border-box;
}

.footer > ul > li:last-child {
  border-right: none;
}

.footer > ul > li > .dual-stack {
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.footer > ul > li > .dual-stack > div {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-base);
}

/* ========================================
   프린트 스타일
======================================== */
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #module-CA-view {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    z-index: 99999999999;
    background-color: white !important;
    padding: 8px 16px !important;
    box-sizing: border-box !important;
    gap: 4px !important;
  }

  .footer,
  .compare-toggle,
  .compare-sticky-bar,
  .compare-divider,
  .plan-label,
  .feedback-canvas-container,
  .feedback-toolbar,
  .feedback-comment-panel,
  .feedback-actions,
  .feedback-view-actions,
  .feedback-mode-badge {
    display: none !important;
  }

  .main-header > p {
    display: none !important;
  }

  .header > .right {
    display: none !important;
  }

  .chart > .plan.compare-plan {
    border: 1px solid var(--color-text-secondary) !important;
  }

  #module-CA-view:not(.print-excel) {
    position: absolute !important;
    height: 100vh !important;
    background:#fff;
    overflow: visible !important;
    padding: 6px 12px !important;
    gap: 4px !important;
  }

  #module-CA-view:not(.print-excel) .excel-table {
    display: none !important;
  }

  #module-CA-view:not(.print-excel) #etc-notice-sp {
    position: static !important;
    display: block !important;
    text-align: right;
    font-size: 10px !important;
    margin: 0 !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list .none {
    display: none !important;
  }

  #module-CA-view:not(.print-excel) .main-header {
    padding: 0 !important;
    height: 20px !important;
    margin: 0 !important;
    min-height: unset !important;
  }

  #module-CA-view:not(.print-excel) .main-header h2 {
    font-size: 14px !important;
    margin: 0 !important;
  }

  #module-CA-view:not(.print-excel) .main-header > div {
    gap: 8px !important;
  }

  #module-CA-view:not(.print-excel) .main-header img {
    width: 18px !important;
    height: 18px !important;
  }

  #module-CA-view:not(.print-excel) .header {
    height: 22px !important;
    min-height: unset !important;
    margin: 0 !important;
  }

  #module-CA-view:not(.print-excel) .header > div {
    gap: 8px !important;
  }

  #module-CA-view:not(.print-excel) .header > div > div {
    border: none !important;
    padding: 3px 8px !important;
    height: auto !important;
    gap: 4px !important;
  }

  #module-CA-view:not(.print-excel) .header > div > div > span,
  #module-CA-view:not(.print-excel) .header > div > div > small {
    font-size: 14px !important;
  }

  #module-CA-view:not(.print-excel) .header > div > div > img {
    width: 16px !important;
    height: 16px !important;
  }

  #module-CA-view:not(.print-excel) .contents {
    border: 1px solid var(--color-text-secondary);
    border-radius: 6px !important;
    min-height: 20px !important;
    padding: 6px 0px !important;
  }

  #module-CA-view:not(.print-excel) .contents > .chapter {
    height: 22px !important;
    background-color: var(--tranditanl-color);
  }

  #module-CA-view:not(.print-excel) .contents > .chapter > h2 {
    font-size: 14px !important;
  }

  #module-CA-view:not(.print-excel) .chapter-diff-badge {
    display: none !important;
  }

  #module-CA-view:not(.print-excel) .chart {
    height: fit-content !important;
    max-height: none !important;
    overflow: visible !important;
    -webkit-overflow-scrolling: auto !important;
    padding-bottom: 0 !important;
    gap: 6px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan {
    background: var(--tranditanl-color) !important;
    border-radius: 6px !important;
    border: 1px solid var(--color-text-secondary) !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .plan-info {
    position: relative !important;
    height: 30px !important;
    padding: 3px 8px !important;
    gap: 4px !important;
    background: var(--tranditanl-color);
    border-bottom: 1px solid var(--tranditanl-color-light) !important;
    break-after: avoid;
    page-break-after: avoid;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .plan-info > ul {
    gap: 8px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .plan-info > ul > li > img {
    width: 18px !important;
    height: 18px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .plan-info > ul > li > span,
  #module-CA-view:not(.print-excel) .chart > .plan > .plan-info > ul > li > h2 {
    font-size: 14px !important;
    margin: 0 !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list {
    padding: 3px !important;
    padding-bottom: 16px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list > ul {
    gap: 2px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list > ul > li {
    font-size: 11px !important;
    flex-direction: row !important;
    align-items: center !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list > ul > li > span {
    font-size: 11px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list > .other {
    height: 14px !important;
  }

  #module-CA-view:not(.print-excel) .chart > .plan > .coverage-list > .coverage > .list > .other > span {
    font-size: 9px !important;
  }

  #module-CA-view:not(.print-excel) .coverage-list {
    overflow: visible !important;
    border-radius: 0px 0px 6px 6px !important;
  }

  /* 표형 인쇄 */
  #module-CA-view.print-excel .chart,
  #module-CA-view.print-excel .contents,
  #module-CA-view.print-excel #etc-notice-sp,
  #module-CA-view.print-excel .main-header,
  #module-CA-view.print-excel .header {
    display: none !important;
  }

  #module-CA-view.print-excel {
    position: absolute !important;
    left: 0;
    top: 0;
    z-index: 999999999;
    height: 100vh !important;
    overflow: visible !important;
    padding: 4px 8px !important;
  }

  #module-CA-view.print-excel .excel-table {
    display: block !important;
    width: 100%;
    overflow: visible !important;
    position: static !important;
  }

  #module-CA-view.print-excel .excel-header {
    margin-bottom: 4px;
  }

  #module-CA-view.print-excel .excel-title h1 {
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    margin: 0 0 4px 0;
    padding: 0;
    text-decoration: underline;
  }

  #module-CA-view.print-excel .excel-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    padding: 0 2px;
  }

  #module-CA-view.print-excel .excel-info strong {
    font-weight: bold;
  }

  #module-CA-view.print-excel .excel-info .unit {
    color: #666;
  }

  #module-CA-view.print-excel .excel-unified-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    table-layout: fixed;
    page-break-inside: auto;
  }

  #module-CA-view.print-excel .excel-unified-table th,
  #module-CA-view.print-excel .excel-unified-table td {
    border: 1px solid #333;
    padding: 3px 4px;
    text-align: center;
    vertical-align: middle;
    word-break: keep-all;
  }

  #module-CA-view.print-excel .excel-unified-table thead {
    display: table-header-group;
  }

  #module-CA-view.print-excel .excel-unified-table thead th {
    background-color: var(--tranditanl-color) !important;
    font-weight: bold;
    font-size: 9px;
    padding: 2px 3px;
  }

  #module-CA-view.print-excel .excel-unified-table th small {
    display: block;
    font-weight: normal;
    font-size: 7px;
    color: #666;
    margin-top: 1px;
  }

  #module-CA-view.print-excel .header-sub th {
    font-size: 8px;
    padding: 2px 3px;
  }

  #module-CA-view.print-excel .col-plan { width: 5%; }
  #module-CA-view.print-excel .col-narrow { width: 8%; }
  #module-CA-view.print-excel .col-coverage { width: 10%; }
  #module-CA-view.print-excel .col-sub { width: 4%; }

  #module-CA-view.print-excel .plan-cell {
    background-color: #f8f8f8 !important;
    vertical-align: middle !important;
    padding: 4px !important;
  }

  #module-CA-view.print-excel .plan-info-compact {
    display: flex;
    flex-direction: column;
    gap: 1px;
    text-align: left;
    line-height: 1.3;
  }

  #module-CA-view.print-excel .plan-info-compact strong { font-size: 12px; font-weight: bold; }
  #module-CA-view.print-excel .plan-info-compact span { font-size: 10px; color: #333; }
  #module-CA-view.print-excel .plan-info-compact small { font-size: 10px; color: #666; }
  #module-CA-view.print-excel .plan-info-compact em { font-size: 10px; font-style: normal; font-weight: bold; color: #000; }

  #module-CA-view.print-excel .cell-stack {
    line-height: 1.4;
    font-size: 11px;
    text-align: left;
    padding: 3px 4px;
  }

  #module-CA-view.print-excel .cell-stack .bold { font-weight: 900; text-decoration: underline; }
  #module-CA-view.print-excel .cell-stack .important { color: #888; }

  #module-CA-view.print-excel .excel-unified-table tbody tr { break-inside: avoid; page-break-inside: avoid; }
  #module-CA-view.print-excel .excel-unified-table tbody td { vertical-align: top; padding: 4px 5px; }

  #module-CA-view.print-excel .excel-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    font-size: 10px;
    color: #666;
  }
}

@page {
  size: A4 landscape;
  margin: 5mm !important;
}

/* ========================================
   피드백 모드 반응형
======================================== */
@media (max-width: 768px) {
  .feedback-toolbar {
    top: auto;
    bottom: 180px;
    left: 10px;
    right: 10px;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .toolbar-section {
    flex-direction: row;
  }
  
  .toolbar-divider {
    width: 2px;
    height: 30px;
    margin: 0 8px;
  }
  
  .feedback-comment-panel {
    width: calc(100% - 40px);
    right: 20px;
    left: 20px;
    bottom: 80px;
  }
  
  .feedback-actions {
    flex-direction: column;
    bottom: 10px;
    right: 10px;
    left: 10px;
  }
  
  .feedback-save-btn,
  .feedback-complete-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>